import { memo, useEffect, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

import { Stepper } from "@mui/material";

import { Logo } from "@zenith/shared";
import { AppStepper } from "@zenith/hooks";
import { getCopyright } from "@zenith/utils";
import { usePersonalDetailsContext } from "@zenith/context";
import {
  outletForDefault,
  outletForExistingAccount,
  outletForMismatch,
  stepBusiness,
  stepDefault,
  stepExistingAccount,
  stepMismatch,
} from "./stepper-helper";
import { StepperStep } from "./StepperStep";
import {
  businessSteps,
  defaultSteps,
  existingAccountSteps,
  mismatchSteps,
  SignUpSteps,
} from "./types";

const INDIVIDUAL_REGISTRATION_TYPE = "INDIVIDUAL";

interface RegistrationStep {
  stepName: string;
  status: "COMPLETED" | "IN_PROGRESS" | "PENDING";
  required: boolean;
}

const SignUpDetailsLayout = memo(function SignUpDetailsLayout() {
  const REGISTRATION_STEP_NAME: Record<string, number> = {
    PENDING_PERSONAL_DETAILS: 0,
    PENDING_SELFIE_VERIFICATION: 1,
    PENDING_BUSINESS_INFORMATION: 2,
    PENDING_CREATE_ACCOUNT: 2,
  };

  const { activeStep, maxStep, setMaxStep, setActiveStep } = AppStepper();
  const location = useLocation();
  const navType = useNavigationType();
  const {
    personalDetailsContext: { mismatchFoundInDocument },
  } = usePersonalDetailsContext();

  const [stepperStep, setStepperStep] = useState<StepperStep>();
  const [signupType, setSignupType] = useState<string>("default");
  const [steps, setSteps] =
    useState<Array<{ stepName: SignUpSteps }>>(defaultSteps);

  const getInitialStepFromRegistrationSteps = (
    registrationSteps: RegistrationStep[]
  ): number => {
    const firstIncomplete = registrationSteps.find(
      (step) => step.status !== "COMPLETED"
    );
    if (!firstIncomplete) return 0;
    return REGISTRATION_STEP_NAME[firstIncomplete.stepName] ?? 0;
  };

  useEffect(() => {
    if (navType !== "PUSH") return;

    const registrationSteps = location.state?.signupType;

    if (Array.isArray(registrationSteps) && registrationSteps.length > 0) {
      setActiveStep(getInitialStepFromRegistrationSteps(registrationSteps));
    } else {
      setActiveStep(0);
    }
  }, [setActiveStep, navType, location.state]);

  useEffect(() => {
    const regType = location.state?.registrationType;
    const isBusinessType = regType && regType !== INDIVIDUAL_REGISTRATION_TYPE;

    if (isBusinessType) {
      setSignupType("business");
      setSteps(businessSteps);
    } else if (mismatchFoundInDocument) {
      setSignupType("mismatch");
      setSteps(mismatchSteps);
    } else {
      setSignupType("default");
      setSteps(defaultSteps);
    }
  }, [mismatchFoundInDocument, location.state]);

  useEffect(() => {
    if (!location.state) return;
    const SIGNUP = location.state.existingAccount
      ? "existingAccount"
      : signupType;
    const STEPS = location.state.existingAccount ? existingAccountSteps : steps;

    if (activeStep >= STEPS.length) {
      return;
    }

    // Mapper for the step components
    const stepComponents: {
      [key: string]: {
        stepper: (steps: Array<{ stepName: SignUpSteps }>) => JSX.Element[];
        outlet: (activeStep: number) => JSX.Element;
      };
    } = {
      default: { stepper: stepDefault, outlet: outletForDefault },
      mismatch: { stepper: stepMismatch, outlet: outletForMismatch },
      business: { stepper: stepBusiness, outlet: outletForDefault },
      existingAccount: {
        stepper: stepExistingAccount,
        outlet: outletForExistingAccount,
      },
    };

    const stepInfo = stepComponents[SIGNUP];
    // Sets the step step length and creates an instance of StepperStep
    // Eventually we might need to store this in context and change the instace creation
    // To the upgrade step entry point
    if (stepInfo) {
      setMaxStep(STEPS.length);
      const stepStepper = new StepperStep(
        SIGNUP,
        stepInfo.stepper(STEPS),
        stepInfo.outlet(activeStep)
      );
      setStepperStep(stepStepper);
    } else {
      console.warn("Step not found");
    }
  }, [setMaxStep, activeStep, location.state, signupType, steps, maxStep]);

  return (
    <div className="flex min-h-screen w-full">
      <div className="relative hidden w-80 flex-col items-start justify-center outline outline-1 outline-stone-300 lg:flex">
        <div className="m-12 flex grow flex-col ">
          <Logo />
          <div className="mt-40 flex w-full grow items-start">
            <Stepper
              activeStep={activeStep}
              orientation="vertical"
              connector={null}
              sx={{
                ".Mui-disabled .StepIcon-circle": {
                  border: "1px solid white",
                  marginTop: "32px",
                },
              }}
            >
              {stepperStep?.stepStepperComponent}
            </Stepper>
          </div>
          <div className="text-gray-300">{getCopyright()}</div>
        </div>
      </div>
      <div
        className="
               my-auto flex h-full w-full flex-col items-center
              justify-center px-10 lg:mx-135  lg:w-auto lg:items-start"
      >
        {stepperStep?.stepStepperOutlet}
      </div>
    </div>
  );
});

export default SignUpDetailsLayout;
